<template>
	<div class="pq-page">
		<div class="pq-inner">
			<div class="pq-title">{{ pageTitle }}</div>

			<!-- 筛选栏 -->
			<div class="pq-bar">
				<div class="pq-group pq-cascader">
					<label>批发市场：</label>
					<el-cascader
						v-model="marketCascade"
						:options="marketTree"
						:props="cascadeProps"
						placeholder="省份 / 批发市场（可多选）"
						clearable
						collapse-tags
						collapse-tags-tooltip
					/>
				</div>
				<div class="pq-group">
					<label>品种名称：</label>
					<select v-model="filter.variety">
						<optgroup v-for="g in varietyGroups" :key="g.group" :label="g.group">
							<option v-for="o in g.options" :key="o.id" :value="o.id">{{ o.name }}</option>
						</optgroup>
					</select>
				</div>
				<button class="pq-query" :disabled="loadingChart" @click="query">查询</button>
				<button class="pq-reset" @click="reset">重置</button>
				<button class="pq-type" :title="chartType === 'bar' ? '切换折线图' : '切换柱状图'" @click="toggleType">
					<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2"
						stroke-linecap="round" stroke-linejoin="round">
						<polyline points="3,15 8,9 12,12 21,4"></polyline>
					</svg>
				</button>
			</div>

			<!-- 行情图 -->
			<div ref="quoteChart" class="pq-chart"></div>

			<!-- 免责声明 -->
			<div class="pq-disclaim">免责声明：本网发布的大宗价数据为各农产品批发市场上报的各类农产品大宗交易均价，这些数据仅供用户参考之用。用户应本着审慎原则使用数据，因数据使用而引起的风险由用户自担，与本网无关。</div>
		</div>
	</div>
</template>

<script>
	import * as echarts from 'echarts'

	// 默认选中品种：猪肉(白条猪)，与来源页一致
	const DEFAULT_VARIETY_ID = '1492'

	export default {
		name: 'PriceQuote',
		data() {
			return {
				dateText: '',
				chartType: 'bar',
				filter: { variety: DEFAULT_VARIETY_ID },
				// 省份→市场 级联多选：值为“路径数组”的集合，[省编码] 或 [省编码, 市场编号]
				marketCascade: [],
				cascadeProps: { multiple: true, checkStrictly: true, emitPath: true, value: 'value', label: 'label', children: 'children' },
				// /filters 拉取的筛选条件
				provinceOptions: [], // [{ id, name }]
				marketOptions: [],   // [{ id, name, parent(省份编码) }]
				varietyGroups: [],   // [{ group(大类), options:[{ id, name }] }]
				// /chart 返回的行情数据
				chartX: [],
				chartY: [],
				loadingChart: false,
				_chart: null,
				_resizeHandler: null,
			}
		},
		computed: {
			pageTitle() {
				return `${this.dateText || '—'}农业农村部全国农产品批发市场价格行情`
			},
			// 省份→批发市场 两级树（无归属省份的市场不展示，与来源页一致）
			marketTree() {
				return this.provinceOptions.map(p => ({
					value: p.id,
					label: p.name,
					children: this.marketOptions
						.filter(m => m.parent === p.id)
						.map(m => ({ value: m.id, label: m.name })),
				}))
			},
		},
		mounted() {
			this.loadFilters()
			this._resizeHandler = () => this._chart && this._chart.resize()
			window.addEventListener('resize', this._resizeHandler)
		},
		beforeDestroy() {
			window.removeEventListener('resize', this._resizeHandler)
			if (this._chart) {
				this._chart.dispose()
				this._chart = null
			}
		},
		methods: {
			// ① 拉取筛选条件：省份 / 市场 / 品种三级树
			loadFilters() {
				this.$request2('/admin-api/governance/pfsc-price-chart/filters').then(res => {
					if (!res || res.code !== 0 || !res.data) return
					const d = res.data
					this.provinceOptions = d.provinces || []
					this.marketOptions = d.markets || []
					this.varietyGroups = this.flattenVarieties(d.varieties || [])
					// 默认品种不存在时回退到第一个可用品种
					if (!this.hasVariety(this.filter.variety)) {
						const first = this.firstVarietyId()
						if (first) this.filter.variety = first
					}
					// 拿到默认品种后立即出图（全国）
					this.loadChart()
				}).catch(() => {})
			},
			// 品种三级树(大类→二级类→品种)扁平化为按大类分组的叶子，供 optgroup 下拉
			flattenVarieties(tree) {
				const groups = []
				tree.forEach(major => {
					const options = []
					;(major.children || []).forEach(sub => {
						;(sub.children || []).forEach(leaf => {
							options.push({ id: leaf.id, name: leaf.name })
						})
					})
					if (options.length) groups.push({ group: major.name, options })
				})
				return groups
			},
			hasVariety(id) {
				return this.varietyGroups.some(g => g.options.some(o => o.id === id))
			},
			firstVarietyId() {
				const g = this.varietyGroups[0]
				return g && g.options[0] ? g.options[0].id : ''
			},
			// ② 查询柱状图：varietyId 必填；勾了市场传 marketIds，否则勾了省份传 provinceCodes，都不传=全国
			loadChart() {
				if (this.loadingChart) return
				const varietyId = this.filter.variety
				if (!varietyId) return
				this.loadingChart = true
				const params = { varietyId: varietyId }
				// 级联选中项：路径长度≥2=具体市场(marketIds)，长度1=整省(provinceCodes)
				const marketIds = []
				const provinceCodes = []
				;(this.marketCascade || []).forEach(path => {
					if (!Array.isArray(path) || !path.length) return
					if (path.length >= 2) marketIds.push(path[path.length - 1])
					else provinceCodes.push(path[0])
				})
				if (marketIds.length) params.marketIds = marketIds.join(',')
				if (provinceCodes.length) params.provinceCodes = provinceCodes.join(',')
				this.$request2('/admin-api/governance/pfsc-price-chart/chart', { params: params }).then(res => {
					this.loadingChart = false
					if (!res || res.code !== 0 || !res.data) {
						this.chartX = []
						this.chartY = []
						this.renderChart()
						return
					}
					const d = res.data
					this.chartX = d.x || []
					this.chartY = (d.y || []).map(v => (v === null || v === '' ? null : Number(v)))
					this.dateText = this.formatDateCN(d.date)
					this.$nextTick(() => this.renderChart())
				}).catch(() => {
					this.loadingChart = false
					this.chartX = []
					this.chartY = []
					this.renderChart()
				})
			},
			formatDateCN(s) {
				if (!s) return ''
				const m = String(s).match(/(\d{4})-(\d{2})-(\d{2})/)
				return m ? m[1] + '年' + m[2] + '月' + m[3] + '日' : String(s).slice(0, 10)
			},
			renderChart() {
				const el = this.$refs.quoteChart
				if (!el) return
				if (!this._chart) this._chart = echarts.init(el)
				const x = this.chartX
				const y = this.chartY
				const isBar = this.chartType === 'bar'
				const hasData = x.length && y.length
				this._chart.setOption({
					backgroundColor: 'transparent',
					tooltip: {
						trigger: 'axis',
						axisPointer: { type: isBar ? 'shadow' : 'line' },
						formatter: p => {
							const it = p && p[0]
							return it ? (it.name + '：' + (it.value == null ? '-' : it.value) + ' 元/公斤') : ''
						},
					},
					grid: { left: 56, right: 24, top: 30, bottom: 118 },
					xAxis: {
						type: 'category',
						data: x,
						axisLine: { lineStyle: { color: '#ccc' } },
						axisTick: { show: false },
						axisLabel: {
							interval: 0, rotate: 35, color: '#666', fontSize: 11,
							formatter: v => (v.length > 9 ? v.slice(0, 9) + '…' : v),
						},
					},
					yAxis: {
						type: 'value', name: '元/公斤', nameTextStyle: { color: '#666' },
						axisLabel: { color: '#666' },
						splitLine: { lineStyle: { type: 'dashed', color: '#eee' } },
					},
					dataZoom: [
						{
							type: 'slider', height: 18, bottom: 24, start: 0, end: 100,
							borderColor: 'transparent', backgroundColor: '#f2f5f7',
							fillerColor: 'rgba(150, 200, 150, .25)', handleStyle: { color: '#cfe3cf' },
							textStyle: { color: '#999' },
						},
						{ type: 'inside' },
					],
					graphic: hasData ? [] : [{
						type: 'text', left: 'center', top: 'middle',
						style: { text: this.loadingChart ? '加载中…' : '该筛选组合当天没有报价数据', fill: '#999', fontSize: 14 },
					}],
					series: [{
						type: this.chartType,
						barWidth: '46%',
						smooth: true,
						showSymbol: false,
						data: y,
						itemStyle: { color: '#a2d18f', borderRadius: isBar ? [3, 3, 0, 0] : 0 },
						lineStyle: { color: '#5aa02c', width: 2 },
						areaStyle: { color: 'rgba(162, 213, 160, .15)' },
					}],
				}, true)
				this._chart.resize()
			},
			query() {
				this.loadChart()
			},
			reset() {
				const variety = this.hasVariety(DEFAULT_VARIETY_ID) ? DEFAULT_VARIETY_ID : this.firstVarietyId()
				this.filter = { variety: variety }
				this.marketCascade = []
				this.loadChart()
			},
			toggleType() {
				this.chartType = this.chartType === 'bar' ? 'line' : 'bar'
				this.renderChart()
			},
		},
	}
</script>

<style scoped lang="scss">
	.pq-page {
		flex: 1;
		min-height: 0;
		overflow: auto;
		background: #fff;
		box-sizing: border-box;
	}

	.pq-inner {
		max-width: 1300px;
		margin: 0 auto;
		padding: 20px;
	}

	.pq-title {
		text-align: center;
		font-size: 22px;
		font-weight: 700;
		color: #333;
		padding: 10px 0 24px;
	}

	/* ===== 筛选栏 ===== */
	.pq-bar {
		display: flex;
		align-items: center;
		flex-wrap: wrap;
		gap: 20px;
		padding: 18px 20px;
		background: #f5f7f9;
		border-radius: 6px;
		box-sizing: border-box;
	}

	.pq-group {
		display: flex;
		align-items: center;
		font-size: 14px;
		color: #666;
	}

	.pq-group label {
		white-space: nowrap;
	}

	.pq-group select {
		height: 34px;
		min-width: 200px;
		border: 1px solid #dcdfe6;
		border-radius: 4px;
		padding: 0 10px;
		font-size: 14px;
		color: #333;
		background: #fff;
		outline: none;
		box-sizing: border-box;
	}

	/* Element 级联选择器宽度/高度对齐原生 select */
	.pq-cascader ::v-deep .el-cascader {
		min-width: 340px;
		font-size: 14px;
	}

	.pq-cascader ::v-deep .el-input__inner {
		height: 34px;
		line-height: 34px;
	}

	.pq-query {
		height: 34px;
		padding: 0 28px;
		background: #22b573;
		color: #fff;
		border: none;
		border-radius: 4px;
		cursor: pointer;
		font-size: 14px;
	}

	.pq-query:hover {
		background: #1ea166;
	}

	.pq-query:disabled {
		opacity: .6;
		cursor: not-allowed;
	}

	.pq-reset {
		height: 34px;
		padding: 0 28px;
		background: #fff;
		color: #22b573;
		border: 1px solid #22b573;
		border-radius: 4px;
		cursor: pointer;
		font-size: 14px;
	}

	.pq-type {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 40px;
		height: 34px;
		background: #fff;
		color: #22b573;
		border: 1px solid #22b573;
		border-radius: 4px;
		cursor: pointer;
	}

	/* ===== 图表 ===== */
	.pq-chart {
		width: 100%;
		height: 480px;
		margin-top: 20px;
	}

	/* ===== 免责声明 ===== */
	.pq-disclaim {
		margin-top: 16px;
		font-size: 13px;
		line-height: 1.8;
		color: #999;
	}
</style>
